/**
 * Topological Sort Utility for Flow Execution
 * 
 * Implements Kahn's algorithm to determine the correct execution order
 * for connected nodes, respecting dependencies (edges).
 * 
 * Returns nodes grouped by "levels" - nodes at the same level can be
 * executed in parallel since they have no dependencies on each other.
 */

import { Node, Edge } from '@xyflow/react';

export interface ExecutionLevel {
  level: number;
  nodeIds: string[];
}

export interface TopologicalSortResult {
  /** Nodes grouped by execution level (parallel-safe within each level) */
  levels: ExecutionLevel[];
  /** Flat list of node IDs in execution order */
  orderedNodeIds: string[];
  /** True if a cycle was detected (invalid for execution) */
  hasCycle: boolean;
  /** Node IDs involved in the cycle, if any */
  cycleNodes: string[];
}

/**
 * Performs topological sort on a flow graph.
 * 
 * @param nodes - All nodes in the flow
 * @param edges - All edges connecting nodes
 * @param startNodeId - Optional: Start from a specific node (for "Run From Here")
 * @param endNodeId - Optional: Stop at a specific node (for segment execution)
 * @returns Sorted nodes grouped by level, or cycle information if invalid
 */
export function topologicalSort(
  nodes: Node[],
  edges: Edge[],
  startNodeId?: string,
  endNodeId?: string
): TopologicalSortResult {
  // Build node ID set for quick lookup
  const nodeIds = new Set(nodes.map(n => n.id));
  
  // Filter to only include nodes we care about
  let relevantNodeIds: Set<string>;
  
  if (startNodeId) {
    // Get all nodes downstream from startNodeId (including itself)
    relevantNodeIds = getDownstreamNodes(startNodeId, edges, nodeIds, endNodeId);
  } else {
    relevantNodeIds = nodeIds;
  }
  
  // Build adjacency list and in-degree map for relevant nodes only
  const graph = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  
  // Initialize
  relevantNodeIds.forEach(id => {
    graph.set(id, []);
    inDegree.set(id, 0);
  });
  
  // Populate from edges
  edges.forEach(edge => {
    if (relevantNodeIds.has(edge.source) && relevantNodeIds.has(edge.target)) {
      graph.get(edge.source)?.push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    }
  });
  
  // Kahn's algorithm with level tracking
  const levels: ExecutionLevel[] = [];
  const orderedNodeIds: string[] = [];
  let processedCount = 0;
  
  // Start with nodes that have no incoming edges (in-degree = 0)
  let currentLevel = Array.from(relevantNodeIds).filter(id => inDegree.get(id) === 0);
  let levelNum = 0;
  
  while (currentLevel.length > 0) {
    // Record this level
    levels.push({
      level: levelNum,
      nodeIds: [...currentLevel]
    });
    orderedNodeIds.push(...currentLevel);
    processedCount += currentLevel.length;
    
    // Find next level
    const nextLevel: string[] = [];
    
    for (const nodeId of currentLevel) {
      const neighbors = graph.get(nodeId) || [];
      for (const neighbor of neighbors) {
        const newDegree = (inDegree.get(neighbor) || 0) - 1;
        inDegree.set(neighbor, newDegree);
        if (newDegree === 0) {
          nextLevel.push(neighbor);
        }
      }
    }
    
    currentLevel = nextLevel;
    levelNum++;
  }
  
  // Check for cycle: if we didn't process all nodes, there's a cycle
  const hasCycle = processedCount < relevantNodeIds.size;
  let cycleNodes: string[] = [];
  
  if (hasCycle) {
    // Find nodes that weren't processed (they're in the cycle)
    cycleNodes = Array.from(relevantNodeIds).filter(
      id => !orderedNodeIds.includes(id)
    );
  }
  
  return {
    levels,
    orderedNodeIds,
    hasCycle,
    cycleNodes
  };
}

/**
 * Get all nodes downstream from a starting node (BFS traversal)
 */
function getDownstreamNodes(
  startNodeId: string,
  edges: Edge[],
  allNodeIds: Set<string>,
  endNodeId?: string
): Set<string> {
  const downstream = new Set<string>();
  const queue = [startNodeId];
  
  // Build adjacency list for forward traversal
  const outgoing = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!outgoing.has(edge.source)) {
      outgoing.set(edge.source, []);
    }
    outgoing.get(edge.source)?.push(edge.target);
  });
  
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    
    if (downstream.has(nodeId)) continue;
    if (!allNodeIds.has(nodeId)) continue;
    
    downstream.add(nodeId);
    
    // Stop at end node if specified
    if (endNodeId && nodeId === endNodeId) continue;
    
    // Add neighbors to queue
    const neighbors = outgoing.get(nodeId) || [];
    queue.push(...neighbors);
  }
  
  return downstream;
}

/**
 * Get all nodes upstream from a node (for finding sources)
 */
export function getUpstreamNodes(
  nodeId: string,
  edges: Edge[],
  allNodeIds: Set<string>
): Set<string> {
  const upstream = new Set<string>();
  const queue = [nodeId];
  
  // Build adjacency list for backward traversal
  const incoming = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!incoming.has(edge.target)) {
      incoming.set(edge.target, []);
    }
    incoming.get(edge.target)?.push(edge.source);
  });
  
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    
    if (upstream.has(currentId)) continue;
    if (!allNodeIds.has(currentId)) continue;
    
    upstream.add(currentId);
    
    // Add predecessors to queue
    const predecessors = incoming.get(currentId) || [];
    queue.push(...predecessors);
  }
  
  return upstream;
}

/**
 * Find source nodes (nodes with no incoming edges)
 */
export function findSourceNodes(nodes: Node[], edges: Edge[]): string[] {
  const hasIncoming = new Set(edges.map(e => e.target));
  return nodes
    .filter(n => !hasIncoming.has(n.id))
    .map(n => n.id);
}

/**
 * Find sink nodes (nodes with no outgoing edges)
 */
export function findSinkNodes(nodes: Node[], edges: Edge[]): string[] {
  const hasOutgoing = new Set(edges.map(e => e.source));
  return nodes
    .filter(n => !hasOutgoing.has(n.id))
    .map(n => n.id);
}

/**
 * Check if there are any cycles in the graph
 */
export function hasCycles(nodes: Node[], edges: Edge[]): boolean {
  const result = topologicalSort(nodes, edges);
  return result.hasCycle;
}
